import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { product, servingOptions, reviews } from '../../mocks/products';
import { useCart, addToCart } from '../../hooks/useCart';

const productImages = [
  { id: 1, src: "https://static.readdy.ai/image/31c717393011dd03ef3cba1fd0ceabcf/da291c2ef815146769ed393cee146c5e.png", label: "Front View" },
  { id: 2, src: "https://static.readdy.ai/image/31c717393011dd03ef3cba1fd0ceabcf/f93ebcd9a706c61bfae408aa830f4a2a.png", label: "Back Label" },
  { id: 3, src: "https://static.readdy.ai/image/31c717393011dd03ef3cba1fd0ceabcf/9a095e4dbac4570c5b1c55bd3cd3ffd6.png", label: "Mix It" },
  { id: 4, src: "https://static.readdy.ai/image/31c717393011dd03ef3cba1fd0ceabcf/3bf65b63b4108cf5caba13c7e53ed896.png", label: "In Use" },
  { id: 5, src: "https://static.readdy.ai/image/31c717393011dd03ef3cba1fd0ceabcf/7d415db1afd0aa2e60083af084a6f903.png", label: "All Sizes" }
];

export default function ProductDetailPage() {
  const [selectedServingId, setSelectedServingId] = useState(servingOptions[1].id);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [activeImage, setActiveImage] = useState(0);
  const [zipCode, setZipCode] = useState('');
  const [shippingEstimate, setShippingEstimate] = useState<{ label: string; days: string; cost: string } | null>(null);
  const [zipError, setZipError] = useState('');
  const [isCalculating, setIsCalculating] = useState(false);
  const [addedToast, setAddedToast] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const { totalCount } = useCart();
  const selectedServing = servingOptions.find(s => s.id === selectedServingId) || servingOptions[1];

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'Front Porch Nectar – Premium Hummingbird Nectar Dry Mix',
      description: '100% natural hummingbird nectar dry mix. No artificial colors, no preservatives. Available in 8oz, 16oz, and 32oz pouches. Just add water and stir.',
      image: [
        'https://static.readdy.ai/image/31c717393011dd03ef3cba1fd0ceabcf/da291c2ef815146769ed393cee146c5e.png',
        'https://static.readdy.ai/image/31c717393011dd03ef3cba1fd0ceabcf/f93ebcd9a706c61bfae408aa830f4a2a.png',
      ],
      brand: {
        '@type': 'Brand',
        name: 'Front Porch Nectar',
      },
      url: `${siteUrl}/product/nectar-pouch`,
      offers: servingOptions.map((opt) => ({
        '@type': 'Offer',
        name: opt.label,
        price: opt.price.toFixed(2),
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        url: `${siteUrl}/product/${opt.id}`,
        shippingDetails: {
          '@type': 'OfferShippingDetails',
          shippingRate: {
            '@type': 'MonetaryAmount',
            value: opt.price >= 25 ? '0.00' : '5.99',
            currency: 'USD',
          },
        },
      })),
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '5.0',
        reviewCount: '127',
        bestRating: '5',
        worstRating: '1',
      },
      review: reviews.slice(0, 3).map((r) => ({
        '@type': 'Review',
        author: { '@type': 'Person', name: r.name },
        reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
        reviewBody: r.text,
      })),
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-product';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById('schema-product');
      if (existing) existing.remove();
    };
  }, []);

  const handleQuantityChange = (delta: number) => {
    setQuantity(Math.max(1, quantity + delta));
  };

  const handleAddToCart = () => {
    addToCart(
      {
        id: selectedServing.id,
        name: `Front Porch Nectar - ${selectedServing.label}`,
        size: selectedServing.label,
        price: selectedServing.price,
        originalPrice: selectedServing.originalPrice,
        image: selectedServing.id === '8srv'
          ? 'https://static.readdy.ai/image/31c717393011dd03ef3cba1fd0ceabcf/e8d1ccb2976c251da973506a218a4c98.png'
          : selectedServing.id === '32srv'
          ? 'https://static.readdy.ai/image/31c717393011dd03ef3cba1fd0ceabcf/3bf65b63b4108cf5caba13c7e53ed896.png'
          : product.image,
      },
      quantity
    );
    setAddedToast(true);
    setTimeout(() => setAddedToast(false), 2500);
  };

  const handleShippingCalc = () => {
    if (!/^\d{5}$/.test(zipCode)) {
      setZipError('Please enter a valid 5-digit ZIP code.');
      setShippingEstimate(null);
      return;
    }
    setZipError('');
    setIsCalculating(true);
    setTimeout(() => {
      const prefix = parseInt(zipCode.slice(0, 3), 10);
      let estimate;
      if (prefix >= 100 && prefix <= 299) {
        estimate = { label: 'Standard Shipping', days: '2–4 business days', cost: selectedServing.price >= 25 ? 'FREE' : '$3.99' };
      } else if (prefix >= 300 && prefix <= 599) {
        estimate = { label: 'Standard Shipping', days: '3–5 business days', cost: selectedServing.price >= 25 ? 'FREE' : '$4.99' };
      } else {
        estimate = { label: 'Standard Shipping', days: '4–7 business days', cost: selectedServing.price >= 25 ? 'FREE' : '$5.99' };
      }
      setShippingEstimate(estimate);
      setIsCalculating(false);
    }, 800);
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Added to Cart Toast */}
      {addedToast && (
        <div className="fixed top-4 right-4 left-4 sm:left-auto sm:right-6 z-[100] bg-[#2C3E50] text-white px-5 py-4 rounded-xl shadow-xl flex items-center gap-3">
          <i className="ri-checkbox-circle-fill text-[#4ECDC4] text-xl w-6 h-6 flex items-center justify-center flex-shrink-0"></i>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">Added to cart!</p>
            <p className="text-xs text-white/70">{selectedServing.label} × {quantity}</p>
          </div>
          <Link to="/checkout" className="text-xs text-[#4ECDC4] font-semibold hover:underline whitespace-nowrap cursor-pointer">View Cart →</Link>
        </div>
      )}

      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white shadow-sm px-4 sm:px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center flex-shrink-0">
            <img
              src="https://public.readdy.ai/ai/img_res/b04a437c-0005-402a-b32c-9c2676d2dc8b.png"
              alt="Front Porch Nectar Logo"
              className="h-10 sm:h-14 w-auto max-w-[160px] sm:max-w-none object-contain"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-[#2C3E50] font-medium hover:text-[#FF6B6B] transition-colors whitespace-nowrap cursor-pointer">Home</Link>
            <Link to="/product/nectar-pouch" className="text-[#2C3E50] font-medium hover:text-[#FF6B6B] transition-colors whitespace-nowrap cursor-pointer">Products</Link>
            <Link to="/migration" className="text-[#2C3E50] font-medium hover:text-[#FF6B6B] transition-colors whitespace-nowrap cursor-pointer">Migration</Link>
            <Link to="/checkout" className="relative cursor-pointer">
              <i className="ri-shopping-cart-line text-2xl text-[#2C3E50] hover:text-[#FF6B6B] transition-colors"></i>
              {totalCount > 0 && (
                <span className="absolute -top-2 -right-2 w-5 h-5 bg-[#FF6B6B] text-white text-xs rounded-full flex items-center justify-center">
                  {totalCount}
                </span>
              )}
            </Link>
          </div>

          {/* Mobile Nav Icons */}
          <div className="flex md:hidden items-center gap-4">
            <Link to="/checkout" className="relative cursor-pointer">
              <i className="ri-shopping-cart-line text-2xl text-[#2C3E50]"></i>
              {totalCount > 0 && (
                <span className="absolute -top-2 -right-2 w-5 h-5 bg-[#FF6B6B] text-white text-xs rounded-full flex items-center justify-center">
                  {totalCount}
                </span>
              )}
            </Link>
            <button
              onClick={() => setMobileMenuOpen(prev => !prev)}
              className="text-[#2C3E50] cursor-pointer w-8 h-8 flex items-center justify-center"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-3 bg-white border-t border-gray-100">
            <Link to="/" onClick={() => setMobileMenuOpen(false)} className="block px-6 py-4 text-[#2C3E50] font-medium border-b border-gray-100 cursor-pointer">Home</Link>
            <Link to="/product/nectar-pouch" onClick={() => setMobileMenuOpen(false)} className="block px-6 py-4 text-[#2C3E50] font-medium border-b border-gray-100 cursor-pointer">Products</Link>
            <Link to="/migration" onClick={() => setMobileMenuOpen(false)} className="block px-6 py-4 text-[#2C3E50] font-medium cursor-pointer">Migration</Link>
          </div>
        )}
      </nav>

      {/* Free Shipping Banner */}
      <div className="bg-[#4ECDC4] text-white text-center py-2.5 px-4">
        <p className="text-xs sm:text-sm font-semibold tracking-wide">
          🚚 FREE SHIPPING on all orders $25 or more — Ships in 1–2 business days!
        </p>
      </div>

      {/* Product Hero */}
      <section className="py-8 sm:py-16 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-16">

            {/* Product Gallery */}
            <div className="flex flex-col gap-4">
              <div className="bg-[#F8F9FA] rounded-2xl overflow-hidden border border-gray-100 relative">
                <img
                  src={productImages[activeImage].src}
                  alt={productImages[activeImage].label}
                  className="w-full h-72 sm:h-[480px] object-contain transition-all duration-300"
                />
                <button
                  onClick={() => setActiveImage((prev) => (prev - 1 + productImages.length) % productImages.length)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/90 rounded-full shadow flex items-center justify-center hover:bg-white transition cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line text-xl text-[#2C3E50]"></i>
                </button>
                <button
                  onClick={() => setActiveImage((prev) => (prev + 1) % productImages.length)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/90 rounded-full shadow flex items-center justify-center hover:bg-white transition cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line text-xl text-[#2C3E50]"></i>
                </button>
              </div>
              <div className="flex justify-center gap-2 mt-1">
                {productImages.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImage(idx)}
                    className={`w-2 h-2 rounded-full transition-all cursor-pointer ${activeImage === idx ? 'bg-[#FF6B6B] w-5' : 'bg-gray-300'}`}
                  />
                ))}
              </div>
            </div>

            {/* Product Info */}
            <div>
              {selectedServing.badge && (
                <span className="inline-block px-4 py-2 bg-[#4ECDC4] text-white text-sm font-bold rounded-full mb-4">
                  {selectedServing.badge}
                </span>
              )}

              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#2C3E50] mb-4 leading-tight">
                {product.name}
              </h1>

              <div className="flex items-center gap-3 mb-6">
                <div className="flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-[#FF6B6B] text-xl"></i>
                  ))}
                </div>
                <span className="text-gray-500 text-sm">(127 reviews)</span>
              </div>

              <div className="flex items-baseline gap-4 mb-6 sm:mb-8">
                <span className="text-4xl sm:text-5xl font-bold text-[#FF6B6B]">${selectedServing.price}</span>
                <span className="text-xl sm:text-2xl text-gray-400 line-through">${selectedServing.originalPrice}</span>
              </div>

              {/* Serving Size Selector */}
              <div className="mb-6 sm:mb-8">
                <label className="block text-sm font-bold text-[#2C3E50] mb-3">SELECT FEEDINGS</label>
                <div className="flex flex-wrap gap-2 sm:gap-3">
                  {servingOptions.map((opt) => (
                    <button
                      key={opt.id}
                      onClick={() => setSelectedServingId(opt.id)}
                      className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer text-sm sm:text-base ${
                        selectedServingId === opt.id
                          ? 'bg-[#FF6B6B] text-white'
                          : 'bg-gray-100 text-[#2C3E50] hover:bg-gray-200'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Quantity Selector */}
              <div className="mb-6">
                <label className="block text-sm font-bold text-[#2C3E50] mb-3">QUANTITY</label>
                <div className="flex items-center gap-4">
                  <button onClick={() => handleQuantityChange(-1)} className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center font-bold text-xl transition-colors cursor-pointer">-</button>
                  <span className="text-2xl font-bold text-[#2C3E50] w-10 text-center">{quantity}</span>
                  <button onClick={() => handleQuantityChange(1)} className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center font-bold text-xl transition-colors cursor-pointer">+</button>
                </div>
              </div>

              {/* Free Shipping Badge */}
              <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-4 py-3 mb-6">
                <i className="ri-truck-line text-green-600 text-xl w-6 h-6 flex items-center justify-center flex-shrink-0"></i>
                <div>
                  <p className="text-green-800 text-sm font-bold">
                    {selectedServing.price >= 25 ? '🎉 You qualify for FREE shipping!' : `Add $${(25 - selectedServing.price).toFixed(2)} more for FREE shipping`}
                  </p>
                  <p className="text-green-700 text-xs">Ships in 1–2 business days · Tracking included</p>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                <button
                  onClick={handleAddToCart}
                  className="block w-full px-8 py-4 bg-[#FF6B6B] text-white text-center rounded-full font-bold text-base sm:text-lg hover:bg-[#ff5252] transition-colors whitespace-nowrap cursor-pointer"
                >
                  ADD TO CART
                </button>
                <button className="w-full px-8 py-4 bg-white border-2 border-[#2C3E50] text-[#2C3E50] rounded-full font-bold text-base sm:text-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer">
                  <i className="ri-heart-line text-xl"></i>
                  ADD TO WISHLIST
                </button>
              </div>

              {/* Shipping Calculator */}
              <div className="bg-[#F8F9FA] rounded-2xl p-4 sm:p-5 mb-6 border border-gray-100">
                <div className="flex items-center gap-2 mb-3">
                  <i className="ri-map-pin-2-line text-[#FF6B6B] text-lg w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                  <span className="font-bold text-[#2C3E50] text-sm">Estimate Shipping</span>
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={zipCode}
                    onChange={(e) => { setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5)); setZipError(''); setShippingEstimate(null); }}
                    placeholder="Enter ZIP code"
                    maxLength={5}
                    className="flex-1 px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:border-[#FF6B6B] focus:outline-none text-sm transition-colors min-w-0"
                  />
                  <button
                    onClick={handleShippingCalc}
                    disabled={isCalculating}
                    className="px-4 sm:px-5 py-2.5 bg-[#2C3E50] text-white rounded-lg font-semibold text-sm hover:bg-[#1a252f] transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
                  >
                    {isCalculating ? 'Checking...' : 'Calculate'}
                  </button>
                </div>
                {zipError && <p className="text-red-500 text-xs mt-2">{zipError}</p>}
                {shippingEstimate && (
                  <div className="mt-3 p-3 bg-white rounded-lg border border-green-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-[#2C3E50]">{shippingEstimate.label}</p>
                        <p className="text-xs text-gray-500">{shippingEstimate.days}</p>
                      </div>
                      <span className={`font-bold text-sm whitespace-nowrap ${shippingEstimate.cost === 'FREE' ? 'text-green-600' : 'text-[#2C3E50]'}`}>
                        {shippingEstimate.cost}
                      </span>
                    </div>
                  </div>
                )}
                <p className="text-xs text-gray-400 mt-2">Free shipping on orders $25+. Rates shown are estimates.</p>
              </div>

              {/* Collapsible Info */}
              <div className="space-y-3">
                {[
                  { key: 'details', label: 'Product Details' },
                  { key: 'ingredients', label: 'Ingredients' },
                  { key: 'instructions', label: 'Feeding Instructions' },
                  { key: 'shipping', label: 'Shipping & Returns' },
                ].map(({ key, label }) => (
                  <div key={key} className="border-b border-gray-200">
                    <button
                      onClick={() => setActiveTab(activeTab === key ? null : key)}
                      className="w-full py-4 flex items-center justify-between text-left cursor-pointer"
                    >
                      <span className="font-bold text-[#2C3E50] text-sm sm:text-base">{label}</span>
                      <i className={`ri-arrow-${activeTab === key ? 'up' : 'down'}-s-line text-xl flex-shrink-0`}></i>
                    </button>
                    {activeTab === key && (
                      <div className="pb-4 text-gray-600 text-sm sm:text-base">
                        {key === 'details' && (
                          <>
                            <p className="mb-4">{product.description}</p>
                            <ul className="space-y-2">
                              {product.features.map((feature, idx) => (
                                <li key={idx} className="flex items-start gap-2">
                                  <i className="ri-check-line text-[#4ECDC4] text-xl flex-shrink-0"></i>
                                  <span>{feature}</span>
                                </li>
                              ))}
                            </ul>
                          </>
                        )}
                        {key === 'ingredients' && (
                          <p>100% pure cane sugar. No artificial colors, flavors, or preservatives. Our formula mimics the natural sucrose content found in flower nectar, providing optimal nutrition for hummingbirds.</p>
                        )}
                        {key === 'instructions' && (
                          <ol className="list-decimal list-inside space-y-2">
                            <li>Mix 1 part nectar powder with 4 parts water</li>
                            <li>Stir until completely dissolved</li>
                            <li>Fill clean feeders and hang in shaded area</li>
                            <li>Replace nectar every 3-5 days or when cloudy</li>
                            <li>Clean feeders with hot water between refills</li>
                          </ol>
                        )}
                        {key === 'shipping' && (
                          <div className="space-y-3">
                            <div className="flex items-start gap-2">
                              <i className="ri-truck-line text-[#4ECDC4] text-lg flex-shrink-0 mt-0.5"></i>
                              <div>
                                <p className="font-semibold text-[#2C3E50]">Free Standard Shipping</p>
                                <p>On all orders $25 or more. Ships in 1–2 business days.</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-2">
                              <i className="ri-time-line text-[#4ECDC4] text-lg flex-shrink-0 mt-0.5"></i>
                              <div>
                                <p className="font-semibold text-[#2C3E50]">Delivery Time</p>
                                <p>2–7 business days depending on your location.</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-2">
                              <i className="ri-arrow-go-back-line text-[#4ECDC4] text-lg flex-shrink-0 mt-0.5"></i>
                              <div>
                                <p className="font-semibold text-[#2C3E50]">30-Day Returns</p>
                                <p>Not satisfied? Return within 30 days for a full refund — no questions asked.</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="bg-[#F8F9FA] py-12 sm:py-16 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
            {[
              { icon: 'ri-paint-line', title: 'No Artificial Colors', desc: 'Pure and natural formula' },
              { icon: 'ri-drop-line', title: 'Easy to Mix', desc: 'Dissolves instantly' },
              { icon: 'ri-shield-check-line', title: 'Vet Approved', desc: 'Safe for all hummingbirds' },
              { icon: 'ri-map-pin-line', title: 'Made in USA', desc: 'Quality you can trust' },
            ].map((feature, idx) => (
              <div key={idx} className="text-center">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#FF6B6B] rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <i className={`${feature.icon} text-2xl sm:text-3xl text-white`}></i>
                </div>
                <h3 className="font-bold text-[#2C3E50] mb-1 text-sm sm:text-base">{feature.title}</h3>
                <p className="text-gray-600 text-xs sm:text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="py-12 sm:py-16 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-4xl font-bold text-[#2C3E50] mb-8 sm:mb-12">CUSTOMER REVIEWS</h2>

          <div className="bg-[#F8F9FA] rounded-2xl p-6 sm:p-8 mb-8 sm:mb-12">
            <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-8 mb-6">
              <div className="text-center">
                <div className="text-5xl sm:text-6xl font-bold text-[#2C3E50] mb-2">5.0</div>
                <div className="flex gap-1 mb-2 justify-center">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-[#FF6B6B] text-xl"></i>
                  ))}
                </div>
                <p className="text-gray-600 text-sm">Based on 127 reviews</p>
              </div>
              <div className="flex-1 w-full">
                {[5, 4, 3, 2, 1].map((stars) => (
                  <div key={stars} className="flex items-center gap-3 mb-2">
                    <span className="text-sm text-gray-600 w-6">{stars}★</span>
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-[#FF6B6B]" style={{ width: stars === 5 ? '100%' : '0%' }}></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-4 sm:space-y-6">
            {reviews.map((review) => (
              <div key={review.id} className="bg-white border border-gray-200 rounded-2xl p-5 sm:p-8">
                <div className="flex items-start gap-3 sm:gap-4 mb-4">
                  <img src={review.avatar} alt={review.name} className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 gap-1">
                      <h4 className="font-bold text-[#2C3E50] text-sm sm:text-base">{review.name}</h4>
                      <span className="text-xs sm:text-sm text-gray-500">{review.date}</span>
                    </div>
                    <div className="flex gap-1">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-[#FF6B6B] text-sm"></i>
                      ))}
                    </div>
                  </div>
                </div>
                <h5 className="font-bold text-[#2C3E50] mb-2 text-sm sm:text-base">{review.title}</h5>
                <p className="text-gray-600 mb-4 text-sm sm:text-base">{review.text}</p>
                <button className="text-sm text-gray-500 hover:text-[#2C3E50] transition-colors cursor-pointer">
                  Helpful ({review.helpful})
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#FF6B6B] text-white py-12 sm:py-16 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 sm:gap-12 mb-10 sm:mb-12">
            <div>
              <img src="https://public.readdy.ai/ai/img_res/b04a437c-0005-402a-b32c-9c2676d2dc8b.png" alt="Front Porch Nectar" className="h-14 sm:h-20 w-auto max-w-[180px] object-contain mb-5 sm:mb-6" />
              <p className="text-white/90 leading-relaxed mb-5 sm:mb-6 text-sm sm:text-base">
                Premium hummingbird nectar made with 100% natural ingredients. Trusted by bird lovers across North America for over a decade.
              </p>
              <div className="flex gap-3">
                <a href="#" className="w-10 h-10 border-2 border-white rounded-full flex items-center justify-center hover:bg-white hover:text-[#FF6B6B] transition-all cursor-pointer"><i className="ri-facebook-fill"></i></a>
                <a href="#" className="w-10 h-10 border-2 border-white rounded-full flex items-center justify-center hover:bg-white hover:text-[#FF6B6B] transition-all cursor-pointer"><i className="ri-instagram-line"></i></a>
                <a href="#" className="w-10 h-10 border-2 border-white rounded-full flex items-center justify-center hover:bg-white hover:text-[#FF6B6B] transition-all cursor-pointer"><i className="ri-twitter-x-line"></i></a>
                <a href="#" className="w-10 h-10 border-2 border-white rounded-full flex items-center justify-center hover:bg-white hover:text-[#FF6B6B] transition-all cursor-pointer"><i className="ri-youtube-fill"></i></a>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-bold mb-4 tracking-wider">CONTACT</h3>
              <p className="mb-6 text-sm sm:text-base break-all">support@frontporchnectar.com</p>
            </div>
            <div className="sm:col-span-2 lg:col-span-1">
              <img
                src="https://public.readdy.ai/ai/img_res/0907509f34839b89e696e1b0a3430f7a.jpg"
                alt="Hummingbird at feeder"
                className="w-full h-56 sm:h-80 object-cover rounded-2xl"
              />
            </div>
          </div>
          <div className="border-t border-white/20 pt-6 sm:pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-white/80 text-sm text-center sm:text-left">© 2025 Front Porch Nectar. All rights reserved</p>
            <div className="flex gap-4 sm:gap-6 text-sm flex-wrap justify-center">
              <a href="#" className="text-white/80 hover:text-white transition-colors cursor-pointer">Privacy Policy</a>
              <span className="text-white/40">/</span>
              <a href="#" className="text-white/80 hover:text-white transition-colors cursor-pointer">Terms</a>
              <span className="text-white/40">/</span>
              <a href="#" className="text-white/80 hover:text-white transition-colors cursor-pointer">Shipping</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
